// Pure motion helpers for the Firefly lab renderer — pulse keyframes,
// animation strings, and highlight/anchor timing. Kept out of Firefly.tsx
// so the component stays focused on the React state machine.

import type { FireflyIntentVisualParams } from "./fireflyIntents";

/** CSS @keyframes injected once per Firefly mount. */
export function fireflyPulseKeyframesCss(): string {
  return `
    @keyframes firefly-lab-pulse-sine {
      0%, 100% { transform: scale(0.85); opacity: 0.65; }
      50%      { transform: scale(1.0);  opacity: 1; }
    }
    @keyframes firefly-lab-pulse-heartbeat {
      0%, 100% { transform: scale(0.78); opacity: 0.5; }
      12%      { transform: scale(1.08); opacity: 1; }
      24%      { transform: scale(0.82); opacity: 0.65; }
      36%      { transform: scale(1.02); opacity: 0.95; }
    }
    @keyframes firefly-lab-pulse-entry {
      0%   { transform: scale(0.65); opacity: 0.35; }
      22%  { transform: scale(1.18); opacity: 1; }
      100% { transform: scale(0.85); opacity: 0.65; }
    }
    @keyframes firefly-lab-hover-drift {
      0%, 100% { transform: translate(0px, 0px); }
      25%      { transform: translate(var(--ff-hover-x), calc(var(--ff-hover-y) * -0.6)); }
      50%      { transform: translate(calc(var(--ff-hover-x) * -0.5), var(--ff-hover-y)); }
      75%      { transform: translate(calc(var(--ff-hover-x) * 0.7), calc(var(--ff-hover-y) * 0.4)); }
    }
  `;
}

export function pulseAnimationStyle(
  params: FireflyIntentVisualParams,
  teachingEntryPlaying: boolean,
): string {
  if (teachingEntryPlaying && params.entryPulseMs) {
    return `firefly-lab-pulse-entry ${params.entryPulseMs}ms ease-out 1, firefly-lab-pulse-sine ${params.pulseDurationMs}ms ease-in-out infinite`;
  }
  const name =
    params.pulseKind === "heartbeat"
      ? "firefly-lab-pulse-heartbeat"
      : "firefly-lab-pulse-sine";
  return `${name} ${params.pulseDurationMs}ms ease-in-out infinite`;
}

export function hoverDriftStyle(params: FireflyIntentVisualParams): {
  animation: string;
  cssVars: Record<string, string>;
} {
  const r = params.hoverRadiusPx;
  return {
    animation: `firefly-lab-hover-drift ${params.hoverDurationMs}ms ease-in-out infinite`,
    cssVars: {
      "--ff-hover-x": `${r}px`,
      "--ff-hover-y": `${Math.round(r * 0.75)}px`,
    },
  };
}

/** Split flicker total into fade-out / dark / fade-in (V4 lean). */
export function flickerPhaseMs(totalMs: number): {
  fadeOutMs: number;
  darkGapMs: number;
  fadeInMs: number;
} {
  const fadeOutMs = Math.round(totalMs * 0.33);
  const darkGapMs = Math.round(totalMs * 0.17);
  const fadeInMs = totalMs - fadeOutMs - darkGapMs;
  return { fadeOutMs, darkGapMs, fadeInMs };
}

/** V9 — one leg of a soft crossfade (no dark gap, no pulse). */
export const FIREFLY_REDUCED_CROSSFADE_MS = 450;

/** Brief dwell at highlight point before returning home (reduced motion). */
export const FIREFLY_REDUCED_HIGHLIGHT_DWELL_MS = 400;

export function reducedMotionCrossfadeTotalMs(): number {
  return FIREFLY_REDUCED_CROSSFADE_MS * 2;
}

/** Remote dwell between the two legs of one highlight cycle. */
export const FIREFLY_HIGHLIGHT_REMOTE_DWELL_MS = 350;

/** Scale fixed highlight pauses with motion speed (matches intent timings). */
export function scaledHighlightRemoteDwellMs(
  motionSpeedMultiplier: number,
): number {
  if (motionSpeedMultiplier <= 0) return FIREFLY_HIGHLIGHT_REMOTE_DWELL_MS;
  return Math.max(
    150,
    Math.round(FIREFLY_HIGHLIGHT_REMOTE_DWELL_MS / motionSpeedMultiplier),
  );
}

export function scaledReducedHighlightDwellMs(
  motionSpeedMultiplier: number,
): number {
  if (motionSpeedMultiplier <= 0) return FIREFLY_REDUCED_HIGHLIGHT_DWELL_MS;
  return Math.max(
    150,
    Math.round(FIREFLY_REDUCED_HIGHLIGHT_DWELL_MS / motionSpeedMultiplier),
  );
}

/** Total ms for one full highlight out-and-back (V5 scheduling aid). */
export function estimateHighlightCycleMs(
  params: FireflyIntentVisualParams,
  reducedMotion: boolean,
  motionSpeedMultiplier: number = 1,
): number {
  if (reducedMotion) {
    return (
      reducedMotionCrossfadeTotalMs() +
      scaledReducedHighlightDwellMs(motionSpeedMultiplier) +
      reducedMotionCrossfadeTotalMs()
    );
  }
  const remote = scaledHighlightRemoteDwellMs(motionSpeedMultiplier);
  return params.flickerDurationMs + remote + params.flickerDurationMs;
}

/** Keep auto-dwell from firing again before a highlight cycle finishes. */
export function effectiveFireflyDwellMs(
  dwellDurationMs: number,
  params: FireflyIntentVisualParams,
  motionSpeedMultiplier: number,
  reducedMotion: boolean,
): number {
  const cycle = estimateHighlightCycleMs(
    params,
    reducedMotion,
    motionSpeedMultiplier,
  );
  return Math.max(dwellDurationMs, cycle + 200);
}

/** @deprecated Use estimateHighlightCycleMs with motionSpeedMultiplier. */
export function estimateHighlightCycleMsLegacy(
  params: FireflyIntentVisualParams,
  reducedMotion: boolean,
): number {
  return estimateHighlightCycleMs(params, reducedMotion, 1);
}
